
import { useState, useEffect, useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getAvailableTimeSlots } from "@/lib/availability/slot-calculator";
import { fetchHostAvailability } from "@/lib/calendar/host-availability";
import { fetchHostAvailabilitySettings } from "@/lib/availability/settings-service";
import { fetchWeeklyAvailabilityWindow } from "@/lib/availability/weekly-availability-service";
import { useDayAvailability } from "./use-day-availability";
import { createPerformanceLogger } from "@/lib/utils/performance-logger";
import { getDateKeyInTimezone } from "@/lib/timezone/date-utils";

const logger = createPerformanceLogger('CalendarSlotsEnhanced');

export interface EnhancedCalendarSlotsParams {
  hostUserId?: string;
  energyPreference?: string;
  duration?: number;
  selectedDates: Date[];
  timezone?: string;
  troubleshootMode?: boolean;
  showAllSlots?: boolean;
  excludeEventId?: string; // NEW: exclude specific calendar event (for reschedule mode)
}

export interface SlotsByType {
  preferred: string[];
  available: string[];
}

export interface EnhancedCalendarSlotsResult {
  availableSlots: Record<string, SlotsByType>;
  unavailableSlots: Record<string, { time: string; reason: string; type: "calendar" | "energy" | "availability" | "buffer"; }[]>;
  loading: boolean;
  error: string | null;
  hasConnection: boolean | null;
  isDayAvailable: (date: Date) => boolean;
  refreshSlots: () => void;
}

// Generate month range from dates
function getMonthRange(dates: Date[]) {
  if (dates.length === 0) return { start: new Date(), end: new Date() };
  
  const sortedDates = dates.sort((a, b) => a.getTime() - b.getTime());
  const firstDate = sortedDates[0];
  const lastDate = sortedDates[sortedDates.length - 1];
  
  // Start from beginning of first month
  const start = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  // End at end of last month
  const end = new Date(lastDate.getFullYear(), lastDate.getMonth() + 1, 0, 23, 59, 59, 999);
  
  return { start, end };
}

export function useCalendarSlotsEnhanced({
  hostUserId,
  energyPreference = "focus",
  duration = 30,
  selectedDates,
  timezone = 'UTC',
  troubleshootMode = false,
  showAllSlots = false,
  excludeEventId // NEW: exclude specific calendar event (for reschedule mode)
}: EnhancedCalendarSlotsParams): EnhancedCalendarSlotsResult {
  
  const [availableSlots, setAvailableSlots] = useState<Record<string, SlotsByType>>({});
  const [unavailableSlots, setUnavailableSlots] = useState<Record<string, { time: string; reason: string; type: "calendar" | "energy" | "availability" | "buffer"; }[]>>({});
  const [hasConnection, setHasConnection] = useState<boolean | null>(null);
  
  const { isDayAvailable } = useDayAvailability(hostUserId, timezone);

  // Memoize month range to prevent unnecessary recalculations
  const monthRange = useMemo(() => getMonthRange(selectedDates), [selectedDates]);

  // Stable query key for better caching
  const queryKey = useMemo(() => [
    'calendar-slots-enhanced-batch',
    hostUserId,
    energyPreference,
    duration,
    monthRange.start.toISOString(),
    monthRange.end.toISOString(),
    timezone,
    showAllSlots,
    excludeEventId || 'none'
  ], [hostUserId, energyPreference, duration, monthRange.start, monthRange.end, timezone, showAllSlots, excludeEventId]);

  const { data: slotsData, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!hostUserId || selectedDates.length === 0) {
        return { slots: {}, unavailable: {}, hasConnection: false };
      }

      return logger.timeAsync('fetchEnhancedCalendarSlotsBatch', async () => {
        console.log(`[useCalendarSlotsEnhanced] Processing month range ${monthRange.start.toISOString()} to ${monthRange.end.toISOString()}, showAllSlots: ${showAllSlots}`);
        
        // Fetch host settings once
        let settings;
        try {
          settings = await fetchHostAvailabilitySettings(hostUserId);
        } catch (err) {
          console.error('[useCalendarSlotsEnhanced] Error fetching host settings:', err);
          settings = { bufferBefore: 15, bufferAfter: 15, minNoticeTime: 60 };
        }

        // Fetch calendar events for entire month range in one call
        let calendarEvents = [];
        let connectionStatus = false;
        
        try {
          const calendarResponse = await fetchHostAvailability(
            hostUserId,
            monthRange.start.toISOString(),
            monthRange.end.toISOString(),
            timezone,
            excludeEventId
          );
          
          calendarEvents = calendarResponse.items || [];
          connectionStatus = !calendarResponse.error;
          console.log(`[useCalendarSlotsEnhanced] Fetched ${calendarEvents.length} events for entire month range`);
        } catch (calendarErr) {
          console.error(`[useCalendarSlotsEnhanced] Calendar error for month range:`, calendarErr);
        }

        const slots: Record<string, SlotsByType> = {};
        const unavailable: Record<string, { time: string; reason: string; type: "calendar" | "energy" | "availability" | "buffer"; }[]> = {};

        // Fetch weekly availability once for the entire week
        const { batchFetchWeeklyAvailability } = await import("@/lib/availability/batch-weekly-availability-service");
        const weeklyAvailabilityData = await batchFetchWeeklyAvailability(hostUserId);
        console.log(`[useCalendarSlotsEnhanced] Weekly availability data:`, weeklyAvailabilityData);

        // Process each requested date using the batched calendar data
        for (const date of selectedDates) {
          const dateKey = getDateKeyInTimezone(date, timezone);
          const dayOfWeek = date.getDay();
          
          try {
            // Get weekly availability window from already-fetched data
            const weeklyAvailability = weeklyAvailabilityData[dayOfWeek] || {
              startTime: "09:00",
              endTime: "17:00",
              isAvailable: dayOfWeek >= 1 && dayOfWeek <= 5
            };
            
            if (!weeklyAvailability.isAvailable) {
              slots[dateKey] = { preferred: [], available: [] };
              unavailable[dateKey] = [{
                time: "All day",
                reason: "Not available on this day of week",
                type: "availability"
              }];
              continue;
            }

            // Filter calendar events for this specific date
            const dayStart = new Date(date);
            dayStart.setHours(0, 0, 0, 0);
            const dayEnd = new Date(date);
            dayEnd.setHours(23, 59, 59, 999);

            const dayEvents = calendarEvents.filter(event => {
              if (!event.start?.dateTime) return false;
              const eventStart = new Date(event.start.dateTime);
              const eventEnd = new Date(event.end?.dateTime || event.start.dateTime);
              
              // Event overlaps with this day
              return (eventStart >= dayStart && eventStart <= dayEnd) ||
                     (eventEnd >= dayStart && eventEnd <= dayEnd) ||
                     (eventStart <= dayStart && eventEnd >= dayEnd);
            });

            // Generate all available time slots
            const availabilityWindow = {
              startTime: weeklyAvailability.startTime,
              endTime: weeklyAvailability.endTime
            };

            const allTimeSlots = await getAvailableTimeSlots({
              date,
              events: dayEvents,
              settings,
              energyPreference: "any",
              duration,
              hostUserId,
              timezone,
              includeUnavailable: troubleshootMode,
              availabilityWindow: availabilityWindow,
              wakeTime: 7
            });

            // Get energy-matched slots
            const energyMatchedSlots = await getAvailableTimeSlots({
              date,
              events: dayEvents,
              settings,
              energyPreference: energyPreference as any,
              duration,
              hostUserId,
              timezone,
              includeUnavailable: false,
              availabilityWindow: availabilityWindow,
              wakeTime: 7
            });

            if (troubleshootMode) {
              const availableSlotTimes = allTimeSlots.filter(slot => slot.isAvailable).map(slot => slot.startTime);
              const preferredSlotTimes = energyMatchedSlots.filter(slot => slot.isAvailable).map(slot => slot.startTime);
              const unavailableItems = allTimeSlots
                .filter(slot => !slot.isAvailable)
                .map(slot => ({
                  time: slot.startTime,
                  reason: slot.reason || "Unknown reason",
                  type: slot.unavailableType || "availability" as const
                }));
              
              slots[dateKey] = {
                preferred: preferredSlotTimes,
                available: availableSlotTimes.filter(time => !preferredSlotTimes.includes(time))
              };
              unavailable[dateKey] = unavailableItems;
            } else {
              const availableSlotTimes = allTimeSlots.filter(slot => slot.isAvailable).map(slot => slot.startTime);
              const preferredSlotTimes = energyMatchedSlots.filter(slot => slot.isAvailable).map(slot => slot.startTime);
              
              if (showAllSlots) {
                slots[dateKey] = {
                  preferred: preferredSlotTimes,
                  available: availableSlotTimes.filter(time => !preferredSlotTimes.includes(time))
                };
              } else {
                slots[dateKey] = {
                  preferred: preferredSlotTimes,
                  available: []
                };
              }
              unavailable[dateKey] = [];
            }

          } catch (dateErr) {
            console.error(`[useCalendarSlotsEnhanced] Error processing date ${dateKey}:`, dateErr);
            slots[dateKey] = { preferred: [], available: [] };
            unavailable[dateKey] = [{
              time: "Error",
              reason: `Failed to load slots: ${dateErr}`,
              type: "availability"
            }];
          }
        }

        console.log(`[useCalendarSlotsEnhanced] Batch processing complete - Processed ${Object.keys(slots).length} dates from ${calendarEvents.length} total events`);
        
        return {
          slots,
          unavailable,
          hasConnection: connectionStatus
        };
      });
    },
    enabled: !!hostUserId && selectedDates.length > 0,
    staleTime: 60000, // 1 minute - reduced from 30s for better performance
    gcTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
    refetchOnMount: false // Prevent unnecessary refetches
  });

  useEffect(() => {
    if (slotsData) {
      setAvailableSlots(slotsData.slots);
      setUnavailableSlots(slotsData.unavailable);
      setHasConnection(slotsData.hasConnection);
    }
  }, [slotsData]);

  const refreshSlots = useCallback(() => {
    console.log('[useCalendarSlotsEnhanced] Manual refresh requested');
    refetch();
  }, [refetch]);

  const errorMessage = error ? (error as Error).message : null;

  return {
    availableSlots,
    unavailableSlots,
    loading: isLoading,
    error: errorMessage,
    hasConnection,
    isDayAvailable,
    refreshSlots
  };
}
